import { ActivityCompletionStatusFilter } from '@cobliteam/api-lib';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import { useEffect, useState } from 'react';
import ActivityStatusItem from './components/ActivityStatusItem';
import ActivityStatusTitle from './components/ActivityStatusTitle';

interface MultiSelectActivityStatusProps {
  size?: 'sm' | 'md';
  selectedOptions: ActivityCompletionStatusFilter[];
  isOpen?: boolean;
  titleComponent?: React.ReactNode;
  onClick?: () => void;
  onChange: (status: ActivityCompletionStatusFilter[]) => void;
}

const MultiSelectActivityStatus = ({ size = 'md', selectedOptions, isOpen, titleComponent, onClick: onDropdownClick, onChange }: MultiSelectActivityStatusProps) => {
  const { t } = useTranslation('multiselects');

  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState<ActivityCompletionStatusFilter[]>(selectedOptions || []);

  const hasSameElements =
    selectedOptions?.length === draft?.length &&
    Array.from(selectedOptions).every((value) => {
      return draft.find((item) => item === value);
    });

  const onClick = () => {
    onDropdownClick?.();
    setDraft(selectedOptions || []);
  };

  const handleClose = () => {
    setForceClosed(true);
    setDraft(selectedOptions || []);
  };

  const handleApply = () => {
    onChange(draft);
    handleClose();
  };

  const toggleItem = (dataItem: ActivityCompletionStatusFilter) => {
    const isSelected = draft.find((d) => d === dataItem);
    const updatedSelected = isSelected ? draft.filter((d) => d !== dataItem) : [...draft, dataItem];
    setDraft(updatedSelected);
  };

  useEffect(() => {
    setForceClosed(!isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const dataFiltered = Object.values(ActivityCompletionStatusFilter);

  return (
    <Dropdown
      portal={false}
      onOpenChange={onClick}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={() => setDraft([])}>
              {t('activity_status.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={300}
              heightItem={28}
              data={dataFiltered}
              itemComponent={(item: ActivityCompletionStatusFilter) => (
                <ActivityStatusItem status={item} onChange={() => toggleItem(item)} checked={draft.some((d) => d === item)} />
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button variant='ghost' full size='sm' onClick={handleClose}>
                {t('activity_status.cancel')}
              </Button>
              <Button size='sm' full onClick={handleApply} disabled={hasSameElements}>
                {t('activity_status.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <div>{titleComponent || <ActivityStatusTitle size={size} selectedStatus={selectedOptions} />}</div>
    </Dropdown>
  );
};

export default MultiSelectActivityStatus;
